import axios from 'axios';

export interface PayerAddress {
  company: string;
  street: string;
  city: string;
  state: string;
  zip: string;
}

interface AddressValidationResult {
  lookupReturnCode: string;
  lookupReturnDescription: string;
  company: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  zipPlusFour: string;
}

interface AddressValidationResponse {
  success: boolean;
  message: string;
  result?: AddressValidationResult;
}

export interface ValidatedPayerAddress extends PayerAddress {
  lookupReturnCode: string;
  lookupReturnDescription: string;
  changedFields: Array<keyof PayerAddress>;
}

const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
const ADDRESS_VALIDATION_URL = `${baseurl}/Payer/validate_and_cleanse_address`;

const normalizeForComparison = (value: string): string =>
  value.trim().replace(/\s+/g, ' ');

export async function validateAndCleansePayerAddress(
  address: PayerAddress,
): Promise<ValidatedPayerAddress> {
  const zipParts = address.zip.trim().split('-');
  const response = await axios.post<AddressValidationResponse>(
    ADDRESS_VALIDATION_URL,
    {
      company: address.company.trim(),
      city: address.city.trim(),
      state: address.state.trim(),
      street: address.street.trim(),
      suite: '',
      zip: zipParts[0],
      zipPlusFour: zipParts[1] || '',
      standardizeStreets: true,
      useMixedCase: true,
      retainAddressLocation: true,
      suiteLinkToAddress2: true,
      useAlias: true,
    },
    {
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json-patch+json',
      },
    },
  );

  if (!response.data.success || !response.data.result) {
    throw new Error(response.data.message || 'Address validation failed.');
  }

  const result = response.data.result;
  const cleansedAddress: PayerAddress = {
    company: result.company || address.company,
    street: result.street || address.street,
    city: result.city || address.city,
    state: result.state || address.state,
    zip: result.zipPlusFour && result.zip
      ? `${result.zip}-${result.zipPlusFour}`
      : result.zip || address.zip,
  };
  const fields: Array<keyof PayerAddress> = ['company', 'street', 'city', 'state', 'zip'];

  return {
    ...cleansedAddress,
    lookupReturnCode: result.lookupReturnCode,
    lookupReturnDescription: result.lookupReturnDescription,
    changedFields: fields.filter(
      (field) => normalizeForComparison(address[field]) !== normalizeForComparison(cleansedAddress[field]),
    ),
  };
}